import React from 'react';
import { EmptyState, ErrorState, LoadingIndicator } from '../../components/ui/States';
import type { IndicativeTotals } from '../../lib/accountsPayableReportsApi';
import { apErrorMessage, isFixableByReader } from './apErrorMessage';
import { formatReportMoney } from './format';
import type { ReportState } from './useReport';

/**
 * The three states every report shares, said in plain language: loading, could not load (never
 * an error code, never the server's prose), and nothing to show. `children` runs only when there
 * is data to draw.
 */
export function ReportFrame<T>({
  state,
  isEmpty,
  emptyTitle,
  emptyDetail,
  children,
}: {
  state: ReportState<T>;
  isEmpty: (data: T) => boolean;
  emptyTitle: string;
  emptyDetail?: string;
  children: (data: T) => React.ReactNode;
}) {
  if (state.error) {
    // A refusal the reader can fix by changing the date or the range needs no "Try again".
    return (
      <ErrorState
        message={apErrorMessage(state.error)}
        onRetry={isFixableByReader(state.error) ? undefined : state.reload}
      />
    );
  }
  if (!state.data) return <LoadingIndicator label="Loading the report…" />;
  return (
    <div aria-busy={state.loading}>
      {state.loading ? <LoadingIndicator label="Updating…" /> : null}
      {isEmpty(state.data) ? <EmptyState title={emptyTitle} detail={emptyDetail} /> : children(state.data)}
    </div>
  );
}

/**
 * The TTD-equivalent of a report, marked indicative. Shown only when more than one currency is
 * in the report, or a currency had no rate to convert with (then it is named and left out): with
 * one currency it would only repeat the block above it.
 */
export const IndicativeLine: React.FC<{
  indicative: IndicativeTotals<string>;
  /** The total of the report this line closes. */
  figureKey: string;
  currencyCount: number;
  what: string;
}> = ({ indicative, figureKey, currencyCount, what }) => {
  const unconverted = indicative.unconvertedCurrencies || [];
  if (currencyCount < 2 && unconverted.length === 0) return null;
  return (
    <p className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
      <span className="font-semibold">Indicative total:</span>{' '}
      <span className="tabular-nums">{formatReportMoney(indicative.totals[figureKey], indicative.currency)}</span>{' '}
      <span>
        ({what}, with other currencies converted to {indicative.currency} at today&apos;s rate. This is an
        estimate; the tables above are the amounts to pay.)
      </span>
      {unconverted.length > 0 ? (
        <span>
          {' '}
          Not included, no exchange rate on file: {unconverted.join(', ')}.
        </span>
      ) : null}
    </p>
  );
};
